import { Box, CircularProgress, Switch } from '@mui/material'
import { DataGrid, GridActionsCellItem, GridToolbar, useGridApiRef } from '@mui/x-data-grid'
import { ConfirmationDialog } from 'components'
import { useSnackbar } from 'notistack'
import { disableRole, enableRole, deleteRole } from 'pages/admin/common/data'
import React, { useEffect, useState } from 'react'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'

export const RolesTable = ({ data, isLoading, setRole }) => {
  const [rowModesModel, setRowModesModel] = useState({})
  const { enqueueSnackbar } = useSnackbar()

  const [rows, setRows] = useState([...(data ?? [])])
  const [roleToDelete, setRoletoDelete] = useState()
  const apiRef = useGridApiRef()

  useEffect(() => {
    setRows(data)
  }, [data])

  const COLUMNS = [
    {
      field: 'Name',
      flex: 1,
      headerName: 'Role name',
      disableColumnMenu: true,
      editable: true
    },
    {
      field: 'Description',
      flex: 1,
      headerName: 'Description',
      disableColumnMenu: true,
      editable: false
    },
    {
      field: 'Customer',
      flex: 1,
      headerName: 'Customer name',
      disableColumnMenu: true,
      editable: false
    },
    {
      field: 'Sites',
      flex: 1,
      headerName: 'Sites',
      disableColumnMenu: true,
      editable: false
    },
    {
      field: 'Enabled',
      type: 'actions',
      flex: 1,
      disableColumnMenu: true,
      editable: true,
      headerName: 'Status',
      cellClassName: 'actions',
      getActions: ({ id, row }) => {
        const name = row.Name
        const isLoading = rowModesModel[id]?.mode === 'status_loading'

        if (isLoading) {
          return [
            <Box
              key="loading"
              sx={{
                width: '14px'
              }}>
              <CircularProgress
                sx={{
                  width: '14px'
                }}
                size="small"
              />
            </Box>
          ]
        }
        return [
          <Switch
            size="small"
            color={row.Enabled ? 'secondary' : 'default'}
            key={id}
            checked={row.Enabled}
            onChange={async (event) => {
              setRowModesModel({ ...rowModesModel, [id]: { mode: 'status_loading' } })
              if (event.target.checked) {
                await handleRoleEnabled(id, name)
              } else {
                await handleUserDisabled(id, name)
              }
              setRowModesModel({ ...rowModesModel, [id]: { mode: undefined } })
            }}
          />
        ]
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      getActions: ({ row, id }) => {
        const isLoading = rowModesModel[id]?.mode === 'loading'

        if (isLoading) {
          return [
            <Box
              key="loading"
              sx={{
                width: '14px'
              }}>
              <CircularProgress
                sx={{
                  width: '14px'
                }}
                size="small"
              />
            </Box>
          ]
        }
        return [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Edit"
            onClick={handleEditClick(row)}
            color="inherit"
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            sx={{
              color: 'error.main'
            }}
            label="Delete"
            onClick={handleDeleteClick(row)}
            color="inherit"
          />
        ]
      }
    }
  ]

  const handleDeleteClick = (row) => () => {
    setRoletoDelete(row)
  }

  const handleEditClick = (row) => () => {
    setRole(row)
  }

  const updateRoleStatus = (index, status) => {
    setRows((rows) => {
      const rows_ = [...rows]
      rows_.splice(index, 1, {
        ...rows[index],
        Enabled: status
      })

      return rows_
    })
  }

  const handleUserDisabled = async (id, name) => {
    enqueueSnackbar(`Disabling ${name}`)
    const index = rows.findIndex((row) => {
      return row.id === id
    })
    updateRoleStatus(index, false)

    try {
      await disableRole(id)
      enqueueSnackbar(`${name} was successfully disabled`)
    } catch {
      updateRoleStatus(index, true)

      enqueueSnackbar(`An error occurred while disabling ${name}`, {
        variant: 'error'
      })
    }
  }

  const handleRoleEnabled = async (id, name) => {
    enqueueSnackbar(`Enabling ${name}`)
    const index = rows.findIndex((row) => {
      return row.id === id
    })

    updateRoleStatus(index, true)
    try {
      await enableRole(id)
      enqueueSnackbar(`${name} was successfully enabled`)
    } catch {
      updateRoleStatus(index, false)
      enqueueSnackbar(`An error occurred while enabling ${name}`, {
        variant: 'error'
      })
    }
  }

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel)
  }

  return (
    <Box
      sx={{
        height: 'calc(100% - 225px)'
      }}>
      <DataGrid
        apiRef={apiRef}
        loading={isLoading}
        showCellVerticalBorder={false}
        getRowId={(data) => {
          return data.id
        }}
        disableMultipleRowSelection
        onRowModesModelChange={handleRowModesModelChange}
        rows={rows}
        editMode="row"
        columns={COLUMNS}
        slots={{ toolbar: GridToolbar }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            csvOptions: {
              allColumns: true
            }
          },
          loadingOverlay: {
            variant: 'skeleton',
            noRowsVariant: 'skeleton'
          }
        }}
      />

      {roleToDelete && (
        <ConfirmationDialog
          saveButtonProps={{
            color: 'error'
          }}
          text={`Are you sure you want to delete '${roleToDelete.Name}'?`}
          onClose={async (result) => {
            if (result === true) {
              enqueueSnackbar({
                message: `Deleting role '${roleToDelete.Name}'`
              })
              setRowModesModel((rowModesModel) => {
                return {
                  ...rowModesModel,
                  [roleToDelete.id]: {
                    mode: 'loading'
                  }
                }
              })

              setRows(rows.filter((row) => row.id !== roleToDelete.id))
              await deleteRole(roleToDelete.id)
              enqueueSnackbar({
                message: `Role '${roleToDelete.Name}' has been deleted`
              })
            }
            setRoletoDelete(undefined)
          }}
        />
      )}
    </Box>
  )
}
